import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlaidLink, PlaidLinkOnSuccess, PlaidLinkOptions } from 'react-plaid-link';
import { apiUrl } from './config';
import { useAuth } from './contexts/AuthContext';
import { Account, Card, HiddenBenefit, RewardBenefit, RewardCardProps, IntervalType } from './types';
import './App.css';

// Helper component for rendering a reward card
const RewardCard: React.FC<RewardCardProps> = ({ benefit, onHide }) => {
  return (
    <div
      style={{
        padding: '16px',
        marginBottom: '10px',
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        borderRadius: '12px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        transition: 'all 0.2s ease',
        cursor: 'default',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
        e.currentTarget.style.transform = 'translateX(4px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
        e.currentTarget.style.transform = 'translateX(0)';
      }}
    >
      <div style={{ flex: 1 }}>
        <strong>{benefit.benefitName}</strong>
        <br />
        <small
          style={{
            opacity: 0.7,
            color: '#61dafb',
            fontSize: '12px',
          }}
        >
          {benefit.cardName}
        </small>
        <br />
        <small style={{ opacity: 0.8 }}>
          {benefit.period}: ${benefit.used.toFixed(2)} / $
          {benefit.limit.toFixed(2)}
        </small>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div
          style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#4caf50',
            minWidth: '100px',
            textAlign: 'right',
          }}
        >
          ${benefit.remaining.toFixed(2)}
        </div>
        <button
          onClick={() => onHide(benefit.benefitId, benefit.accountId)}
          style={{
            padding: '4px 8px',
            fontSize: '12px',
            borderRadius: '6px',
            border: 'none',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            color: 'white',
            cursor: 'pointer',
            opacity: 0.6,
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '1';
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '0.6';
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
          }}
        >
          Hide
        </button>
      </div>
    </div>
  );
};

function App() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, login, logout, getAuthHeaders } = useAuth();
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [linkSuccess, setLinkSuccess] = useState<boolean>(false);
  const [linkedInstitution, setLinkedInstitution] = useState<string>('');
  const [linkedAccounts, setLinkedAccounts] = useState<Account[]>([]);
  const [transactionCount, setTransactionCount] = useState<number>(0);
  const [availableCards, setAvailableCards] = useState<Card[]>([]);
  const [syncingAccountId, setSyncingAccountId] = useState<string | null>(null);
  const [isAccountsExpanded, setIsAccountsExpanded] = useState<boolean>(false);
  const [hiddenBenefits, setHiddenBenefits] = useState<HiddenBenefit[]>([]);
  const [isHiddenExpanded, setIsHiddenExpanded] = useState<boolean>(false);
  const [isRedeemedExpanded, setIsRedeemedExpanded] = useState<boolean>(false);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(false);

  const fetchAvailableCards = useCallback(async (): Promise<void> => {
    try {
      const response = await fetch(apiUrl('/api/available-cards'), {
        headers: {
          ...getAuthHeaders(),
        },
      });
      const data = await response.json();
      if (data.cards) {
        setAvailableCards(data.cards);
      }
    } catch (err) {
      console.error('Failed to fetch available cards', err);
    }
  }, [getAuthHeaders]);

  const fetchHiddenBenefits = useCallback(async (): Promise<void> => {
    try {
      const response = await fetch(apiUrl('/api/hidden-benefits'), {
        headers: {
          ...getAuthHeaders(),
        },
      });
      const data = await response.json();
      if (data.hidden) {
        setHiddenBenefits(data.hidden);
      }
    } catch (err) {
      console.error('Failed to fetch hidden benefits', err);
    }
  }, [getAuthHeaders]);

  const handleHideBenefit = async (benefitId: string, accountId: string): Promise<void> => {
    try {
      const response = await fetch(apiUrl('/api/hide-benefit'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ benefit_id: benefitId, account_id: accountId }),
      });

      const data = await response.json();
      if (data.success) {
        await fetchHiddenBenefits();
      }
    } catch (err) {
      console.error('Failed to hide benefit', err);
    }
  };

  const handleUnhideBenefit = async (benefitId: string, accountId: string): Promise<void> => {
    try {
      const response = await fetch(apiUrl('/api/unhide-benefit'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ benefit_id: benefitId, account_id: accountId }),
      });

      const data = await response.json();
      if (data.success) {
        await fetchHiddenBenefits();
      }
    } catch (err) {
      console.error('Failed to unhide benefit', err);
    }
  };

  const syncTransactions = useCallback(async (): Promise<void> => {
    try {
      const response = await fetch(apiUrl('/api/sync-transactions'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({}),
      });
      const data = await response.json();
      if (data.success && data.synced > 0) {
        console.log(`Synced ${data.synced} account(s)`);
      }
    } catch (err) {
      console.error('Failed to sync transactions', err);
    }
  }, [getAuthHeaders]);

  const fetchLinkedAccounts = useCallback(async (): Promise<void> => {
    try {
      const response = await fetch(apiUrl('/api/linked-accounts'), {
        headers: {
          ...getAuthHeaders(),
        },
      });
      const data = await response.json();
      if (data.accounts) {
        setLinkedAccounts(data.accounts);
      }
    } catch (err) {
      console.error('Failed to fetch linked accounts', err);
    }
  }, [getAuthHeaders]);

  const syncAndFetchAccounts = useCallback(async (): Promise<void> => {
    await syncTransactions();
    await fetchLinkedAccounts();
  }, [syncTransactions, fetchLinkedAccounts]);

  useEffect(() => {
    if (isAuthenticated) {
      const loadInitialData = async () => {
        setIsInitialLoading(true);
        try {
          await Promise.all([
            syncAndFetchAccounts(),
            fetchAvailableCards(),
            fetchHiddenBenefits()
          ]);
        } finally {
          setIsInitialLoading(false);
        }
      };
      loadInitialData();
    }
  }, [isAuthenticated, syncAndFetchAccounts, fetchAvailableCards, fetchHiddenBenefits]);

  const handleUnlinkAccount = async (itemId: string, institutionName: string): Promise<void> => {
    if (!window.confirm(`Are you sure you want to unlink ${institutionName}?`)) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(apiUrl('/api/unlink-account'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ item_id: itemId }),
      });

      const data = await response.json();

      if (data.success) {
        setLinkSuccess(false);
        fetchLinkedAccounts();
      } else {
        setError('Failed to unlink account');
      }
    } catch (err) {
      setError('Failed to unlink account');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLabelCard = async (accountId: string, label: string): Promise<void> => {
    setIsLoading(true);

    try {
      const response = await fetch(apiUrl('/api/label-card'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ account_id: accountId, label }),
      });

      const data = await response.json();

      if (data.success) {
        fetchLinkedAccounts();
      } else {
        setError('Failed to update label');
      }
    } catch (err) {
      setError('Failed to update label');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetRenewalDate = async (accountId: string, renewalDate: string): Promise<void> => {
    setIsLoading(true);

    try {
      const response = await fetch(apiUrl('/api/set-renewal-date'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          account_id: accountId,
          renewal_date: renewalDate,
        }),
      });

      const data = await response.json();

      if (data.success) {
        fetchLinkedAccounts();
      } else {
        setError('Failed to update renewal date');
      }
    } catch (err) {
      setError('Failed to update renewal date');
    } finally {
      setIsLoading(false);
    }
  };

  // Check if a period is current or future (not past)
  const isPeriodCurrentOrFuture = (period: string): boolean => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-indexed

    // Parse different period formats
    if (period.startsWith('Anniversary ')) {
      // Anniversary period: "Anniversary 2025-2026"
      const years = period.replace('Anniversary ', '').split('-');
      const endYear = parseInt(years[1]);
      // Consider current if the end year is current or future
      return endYear >= currentYear;
    } else if (period.includes('-Q')) {
      // Quarterly: "2026-Q1"
      const [year, quarterStr] = period.split('-Q');
      const periodYear = parseInt(year);
      const quarter = parseInt(quarterStr);
      const currentQuarter = Math.floor(currentMonth / 3) + 1;

      return (
        periodYear > currentYear ||
        (periodYear === currentYear && quarter >= currentQuarter)
      );
    } else if (period.includes('-H')) {
      // Semi-annual: "2026-H1"
      const [year, halfStr] = period.split('-H');
      const periodYear = parseInt(year);
      const half = parseInt(halfStr);
      const currentHalf = currentMonth < 6 ? 1 : 2;

      return (
        periodYear > currentYear ||
        (periodYear === currentYear && half >= currentHalf)
      );
    } else if (period.includes('-')) {
      // Monthly: "2026-02"
      const [year, month] = period.split('-');
      const periodYear = parseInt(year);
      const periodMonth = parseInt(month) - 1; // Convert to 0-indexed

      return (
        periodYear > currentYear ||
        (periodYear === currentYear && periodMonth >= currentMonth)
      );
    } else {
      // Yearly: "2026"
      const periodYear = parseInt(period);
      return periodYear >= currentYear;
    }
  };

  // Check if a benefit is hidden
  const isBenefitHidden = (benefitId: string, accountId: string): boolean => {
    return hiddenBenefits.some(
      (hb) => hb.benefit_id === benefitId && hb.account_id === accountId
    );
  };

  // Determine interval type from period key
  const getIntervalType = (period: string): IntervalType => {
    if (period.includes('-Q')) return 'quarterly';
    if (period.includes('-H')) return 'semi-annual';
    if (period.includes('-') && period.match(/^\d{4}-\d{2}$/)) return 'monthly';
    if (period.startsWith('Anniversary')) return 'annual';
    if (period.match(/^\d{4}$/)) return 'annual';
    return 'monthly'; // Default fallback
  };

  // Calculate total unspent rewards across all cards
  const calculateUnspentRewards = (): RewardBenefit[] => {
    const benefitsSummary: Record<string, RewardBenefit> = {};

    linkedAccounts.forEach((account) => {
      account.credit_cards?.forEach((card) => {
        // Get card display name
        const cardName = card.label
          ? availableCards.find((c) => c.id === card.label)?.name || card.label
          : card.name;

        card.benefitUsage?.forEach((benefit) => {
          Object.entries(benefit.usage).forEach(([period, data]) => {
            // Only include current or future periods
            if (
              data.limit &&
              data.limit > 0 &&
              isPeriodCurrentOrFuture(period)
            ) {
              const remaining = data.limit - data.used;
              if (remaining > 0) {
                // Include card name in the key to show which card the benefit is from
                const key = `${benefit.benefitId}-${card.account_id}-${period}`;
                if (!benefitsSummary[key]) {
                  benefitsSummary[key] = {
                    benefitId: benefit.benefitId,
                    benefitName: benefit.benefitName,
                    period,
                    cardName,
                    accountId: card.account_id,
                    interval: getIntervalType(period),
                    used: 0,
                    limit: 0,
                    remaining: 0,
                    hidden: isBenefitHidden(benefit.benefitId, card.account_id),
                  };
                }
                benefitsSummary[key].used += data.used;
                benefitsSummary[key].limit += data.limit;
                benefitsSummary[key].remaining += remaining;
              }
            }
          });
        });
      });
    });

    return Object.values(benefitsSummary);
  };

  // Calculate redeemed rewards (benefits used in current period)
  const calculateRedeemedRewards = (): RewardBenefit[] => {
    const redeemedSummary: Record<string, RewardBenefit> = {};

    linkedAccounts.forEach((account) => {
      account.credit_cards?.forEach((card) => {
        // Get card display name
        const cardName = card.label
          ? availableCards.find((c) => c.id === card.label)?.name || card.label
          : card.name;

        card.benefitUsage?.forEach((benefit) => {
          Object.entries(benefit.usage).forEach(([period, data]) => {
            // Only include current or future periods with usage > 0
            if (
              data.limit &&
              data.limit > 0 &&
              data.used > 0 &&
              isPeriodCurrentOrFuture(period)
            ) {
              const key = `${benefit.benefitId}-${card.account_id}-${period}`;
              if (!redeemedSummary[key]) {
                redeemedSummary[key] = {
                  benefitId: benefit.benefitId,
                  benefitName: benefit.benefitName,
                  period,
                  cardName,
                  accountId: card.account_id,
                  interval: 'monthly', // Default, not used in redeemed view
                  used: 0,
                  limit: 0,
                  remaining: 0,
                  hidden: isBenefitHidden(benefit.benefitId, card.account_id),
                };
              }
              redeemedSummary[key].used += data.used;
              redeemedSummary[key].limit += data.limit;
              redeemedSummary[key].remaining += data.limit - data.used;
            }
          });
        });
      });
    });

    return Object.values(redeemedSummary);
  };

  const handleResyncAccount = async (accountId: string): Promise<void> => {
    setSyncingAccountId(accountId);
    setError('');

    try {
      const response = await fetch(apiUrl('/api/sync-account'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ account_id: accountId }),
      });

      const data = await response.json();

      if (data.success) {
        await fetchLinkedAccounts();
        alert(
          `Sync complete!\n` +
          `Added: ${data.added} | Modified: ${data.modified} | Removed: ${data.removed}\n` +
          `Total transactions: ${data.total}`
        );
      } else {
        setError(data.error || 'Failed to sync account');
      }
    } catch (err) {
      setError('Failed to sync account');
    } finally {
      setSyncingAccountId(null);
    }
  };


  const startPlaidLink = async (): Promise<void> => {
    try {
      const response = await fetch(apiUrl('/api/link-start'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({}),
      });

      const data = await response.json();
      if (data.link_token) {
        setLinkToken(data.link_token);
      } else {
        setError('Failed to start Plaid Link');
      }
    } catch (err) {
      setError('Failed to connect to server');
    }
  };

  const onSuccess: PlaidLinkOnSuccess = useCallback(async (public_token, metadata) => {
    try {
      const response = await fetch(apiUrl('/api/link-exchange'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ public_token, metadata }),
      });

      const data = await response.json();
      if (data.success) {
        setLinkSuccess(true);
        setLinkedInstitution(data.institution_name || 'Account');
        setTransactionCount(data.transactions_fetched || 0);
        setLinkToken(null);
        fetchLinkedAccounts();
      } else {
        setError('Failed to link account');
      }
    } catch (err) {
      setError('Failed to link account');
    }
  }, [getAuthHeaders, fetchLinkedAccounts]);

  const config: PlaidLinkOptions = {
    token: linkToken,
    onSuccess,
  };

  const { open, ready } = usePlaidLink(config);

  useEffect(() => {
    if (linkToken && ready) {
      open();
    }
  }, [linkToken, ready, open]);

  if (isAuthenticated) {
    if (isInitialLoading) {
      return (
        <div className="App">
          <header className="App-header">
            <h1 style={{ marginBottom: '10px' }}>💳 Card Tracker</h1>
            <div
              style={{
                marginTop: '40px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '20px',
              }}
            >
              <div
                style={{
                  width: '50px',
                  height: '50px',
                  border: '4px solid rgba(97, 218, 251, 0.3)',
                  borderTop: '4px solid #61dafb',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
              <p style={{ fontSize: '18px', opacity: 0.8 }}>
                Loading your rewards data...
              </p>
            </div>
            <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>
          </header>
        </div>
      );
    }

    const unspentRewards = calculateUnspentRewards();
    const visibleRewards = unspentRewards.filter(r => !r.hidden);
    const hiddenRewards = unspentRewards.filter(r => r.hidden);

    // Group visible rewards by interval
    const monthlyRewards = visibleRewards.filter(r => r.interval === 'monthly');
    const semiAnnualRewards = visibleRewards.filter(r => r.interval === 'semi-annual');
    const quarterlyRewards = visibleRewards.filter(r => r.interval === 'quarterly');
    const annualRewards = visibleRewards.filter(r => r.interval === 'annual');

    const redeemedRewards = calculateRedeemedRewards();
    const visibleRedeemed = redeemedRewards.filter(r => !r.hidden);

    return (
      <div className="App">
        <header className="App-header">
          <h1 style={{ marginBottom: '10px' }}>💳 Card Tracker</h1>
          {linkSuccess && (
            <div style={{
              marginTop: '20px',
              padding: '16px 24px',
              backgroundColor: 'rgba(76, 175, 80, 0.15)',
              borderRadius: '12px',
              border: '1px solid rgba(76, 175, 80, 0.4)',
              maxWidth: '600px',
            }}>
              <p style={{ color: '#4caf50', margin: 0, fontSize: '16px', fontWeight: '600' }}>
                ✓ {linkedInstitution} linked successfully!
              </p>
              <small style={{ opacity: 0.8, fontSize: '14px' }}>
                Fetched {transactionCount} transactions from the past year
              </small>
            </div>
          )}
          {visibleRewards.length > 0 && (
            <div
              style={{
                marginTop: '30px',
                padding: '25px',
                background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(76, 175, 80, 0.05) 100%)',
                borderRadius: '16px',
                maxWidth: '600px',
                border: '2px solid rgba(76, 175, 80, 0.4)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
              }}
            >
              <h2 style={{ marginTop: 0, color: '#4caf50', fontSize: '24px', fontWeight: '600', marginBottom: '20px' }}>
                💰 Available Rewards
              </h2>
              <div style={{ textAlign: 'left', fontSize: '14px' }}>
                {monthlyRewards.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h3 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      opacity: 0.7,
                      marginBottom: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      📅 Monthly
                    </h3>
                    {monthlyRewards.map((benefit, index) => (
                      <RewardCard key={index} benefit={benefit} onHide={handleHideBenefit} />
                    ))}
                  </div>
                )}
                {quarterlyRewards.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h3 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      opacity: 0.7,
                      marginBottom: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      📊 Quarterly
                    </h3>
                    {quarterlyRewards.map((benefit, index) => (
                      <RewardCard key={index} benefit={benefit} onHide={handleHideBenefit} />
                    ))}
                  </div>
                )}
                {semiAnnualRewards.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h3 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      opacity: 0.7,
                      marginBottom: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      📆 Semi-Annual
                    </h3>
                    {semiAnnualRewards.map((benefit, index) => (
                      <RewardCard key={index} benefit={benefit} onHide={handleHideBenefit} />
                    ))}
                  </div>
                )}
                {annualRewards.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h3 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      opacity: 0.7,
                      marginBottom: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      📅 Annual
                    </h3>
                    {annualRewards.map((benefit, index) => (
                      <RewardCard key={index} benefit={benefit} onHide={handleHideBenefit} />
                    ))}
                  </div>
                )}
                {visibleRewards.length === 0 && (
                  <div style={{ opacity: 0.6, textAlign: 'center', padding: '20px' }}>
                    No available rewards for the current period
                  </div>
                )}
              </div>
            </div>
          )}
          {hiddenRewards.length > 0 && (
            <div
              style={{
                marginTop: '15px',
                padding: '20px',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                maxWidth: '600px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <div
                onClick={() => setIsHiddenExpanded(!isHiddenExpanded)}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: isHiddenExpanded ? '15px' : '0',
                  padding: '5px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '500', opacity: 0.7 }}>
                  👁️‍🗨️ Hidden Rewards ({hiddenRewards.length})
                </h3>
                <span
                  style={{
                    fontSize: '16px',
                    transform: isHiddenExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease',
                    opacity: 0.5,
                  }}
                >
                  ▼
                </span>
              </div>
              {isHiddenExpanded && (
                <div style={{ textAlign: 'left', fontSize: '14px' }}>
                  {hiddenRewards.map((benefit, index) => (
                    <div
                      key={index}
                      style={{
                        padding: '12px',
                        marginBottom: '8px',
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        borderRadius: '8px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        opacity: 0.6,
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <strong style={{ opacity: 0.8 }}>{benefit.benefitName}</strong>
                        <br />
                        <small
                          style={{
                            opacity: 0.6,
                            color: '#61dafb',
                            fontSize: '12px',
                          }}
                        >
                          {benefit.cardName}
                        </small>
                        <br />
                        <small style={{ opacity: 0.7 }}>
                          {benefit.period}: ${benefit.used.toFixed(2)} / $
                          {benefit.limit.toFixed(2)}
                        </small>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div
                          style={{
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: '#888',
                            minWidth: '100px',
                            textAlign: 'right',
                          }}
                        >
                          ${benefit.remaining.toFixed(2)}
                        </div>
                        <button
                          onClick={() => handleUnhideBenefit(benefit.benefitId, benefit.accountId)}
                          style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            borderRadius: '6px',
                            border: 'none',
                            backgroundColor: 'rgba(97, 218, 251, 0.2)',
                            color: '#61dafb',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(97, 218, 251, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(97, 218, 251, 0.2)';
                          }}
                        >
                          Show
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {visibleRedeemed.length > 0 && (
            <div
              style={{
                marginTop: '30px',
                padding: '25px',
                backgroundColor: 'rgba(97, 218, 251, 0.08)',
                borderRadius: '16px',
                maxWidth: '600px',
                border: '1px solid rgba(97, 218, 251, 0.2)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
              }}
            >
              <div
                onClick={() => setIsRedeemedExpanded(!isRedeemedExpanded)}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: isRedeemedExpanded ? '20px' : '0',
                  padding: '10px',
                  borderRadius: '8px',
                  transition: 'background-color 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(97, 218, 251, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <div>
                  <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#61dafb' }}>
                    ✓ Redeemed Rewards
                  </h2>
                </div>
                <span
                  style={{
                    fontSize: '20px',
                    transform: isRedeemedExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease',
                    opacity: 0.7,
                  }}
                >
                  ▼
                </span>
              </div>
              {isRedeemedExpanded && (
                <div style={{ textAlign: 'left', fontSize: '14px' }}>
                  {visibleRedeemed.map((benefit, index) => (
                    <div
                      key={index}
                      style={{
                        padding: '16px',
                        marginBottom: '10px',
                        backgroundColor: 'rgba(97, 218, 251, 0.08)',
                        borderRadius: '12px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        border: '1px solid rgba(97, 218, 251, 0.15)',
                        transition: 'all 0.2s ease',
                        cursor: 'default',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(97, 218, 251, 0.12)';
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(97, 218, 251, 0.08)';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <strong>{benefit.benefitName}</strong>
                        <br />
                        <small
                          style={{
                            opacity: 0.7,
                            color: '#61dafb',
                            fontSize: '12px',
                          }}
                        >
                          {benefit.cardName}
                        </small>
                        <br />
                        <small style={{ opacity: 0.8 }}>
                          {benefit.period}: ${benefit.used.toFixed(2)} / $
                          {benefit.limit.toFixed(2)}
                        </small>
                        {benefit.remaining > 0 && (
                          <>
                            <br />
                            <small style={{ opacity: 0.6, fontSize: '12px' }}>
                              ${benefit.remaining.toFixed(2)} remaining
                            </small>
                          </>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: '24px',
                          fontWeight: 'bold',
                          color: '#61dafb',
                          minWidth: '100px',
                          textAlign: 'right',
                        }}
                      >
                        ${benefit.used.toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {linkedAccounts.length > 0 && (
            <div
              style={{
                marginTop: '30px',
                padding: '25px',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                borderRadius: '16px',
                maxWidth: '600px',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
              }}
            >
              <div
                onClick={() => setIsAccountsExpanded(!isAccountsExpanded)}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: isAccountsExpanded ? '20px' : '0',
                  padding: '10px',
                  borderRadius: '8px',
                  transition: 'background-color 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>🏦 Linked Accounts</h2>
                <span
                  style={{
                    fontSize: '20px',
                    transform: isAccountsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease',
                    opacity: 0.7,
                  }}
                >
                  ▼
                </span>
              </div>
              {isAccountsExpanded && (
              <ul style={{ listStyle: 'none', padding: 0, textAlign: 'left' }}>
                {linkedAccounts.map((account) => (
                  <li
                    key={account.item_id}
                    style={{
                      padding: '10px',
                      marginBottom: '8px',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '4px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <strong>{account.institution_name}</strong>
                      <br />
                      {account.credit_cards && account.credit_cards.length > 0 && (
                        <div style={{ marginTop: '5px', marginBottom: '5px' }}>
                          {account.credit_cards.map((card) => (
                            <div
                              key={card.account_id}
                              style={{
                                fontSize: '14px',
                                opacity: 0.9,
                                marginLeft: '10px',
                                marginBottom: '8px',
                              }}
                            >
                              • {card.name}
                              {card.mask && ` (...${card.mask})`}
                              {card.label && (
                                <span
                                  style={{
                                    marginLeft: '8px',
                                    padding: '2px 8px',
                                    backgroundColor: 'rgba(97, 218, 251, 0.2)',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                  }}
                                >
                                  {
                                    availableCards.find((c) => c.id === card.label)
                                      ?.name || card.label
                                  }
                                </span>
                              )}
                              {card.benefitUsage && card.benefitUsage.length > 0 && (
                                <div
                                  style={{
                                    marginTop: '8px',
                                    marginLeft: '12px',
                                    fontSize: '12px',
                                  }}
                                >
                                  <strong style={{ opacity: 0.8 }}>Benefits:</strong>
                                  {card.benefitUsage.map((benefit) => (
                                    <div
                                      key={benefit.benefitId}
                                      style={{
                                        marginTop: '4px',
                                        padding: '4px 8px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                        borderRadius: '4px',
                                      }}
                                    >
                                      <div style={{ fontWeight: 'bold', opacity: 0.9 }}>
                                        {benefit.benefitName}
                                      </div>
                                      {Object.entries(benefit.usage).map(([period, data]) => (
                                        <div
                                          key={period}
                                          style={{
                                            marginTop: '2px',
                                            opacity: 0.8,
                                          }}
                                        >
                                          {period}:{' '}
                                          <span
                                            style={{
                                              color:
                                                data.limit && data.used >= data.limit
                                                  ? '#4caf50'
                                                  : '#61dafb',
                                            }}
                                          >
                                            ${data.used.toFixed(2)}
                                          </span>
                                          {data.limit && ` / $${data.limit.toFixed(2)}`}
                                        </div>
                                      ))}
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div style={{ marginTop: '4px' }}>
                                <select
                                  value={card.label || ''}
                                  onChange={(e) =>
                                    handleLabelCard(card.account_id, e.target.value)
                                  }
                                  disabled={isLoading}
                                  style={{
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                    borderRadius: '4px',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    color: 'white',
                                    cursor: 'pointer',
                                    width: '100%',
                                  }}
                                >
                                  <option value="">Select card type...</option>
                                  {availableCards.map((availableCard) => (
                                    <option
                                      key={availableCard.id}
                                      value={availableCard.id}
                                    >
                                      {availableCard.name}
                                    </option>
                                  ))}
                                </select>
                                {card.label === 'csr' && (
                                  <div style={{ marginTop: '8px' }}>
                                    <label
                                      style={{
                                        display: 'block',
                                        fontSize: '11px',
                                        opacity: 0.8,
                                        marginBottom: '4px',
                                      }}
                                    >
                                      Card Anniversary Date:
                                    </label>
                                    <input
                                      type="date"
                                      defaultValue={card.renewal_date || ''}
                                      onBlur={(e) => {
                                        if (e.target.value !== card.renewal_date) {
                                          handleSetRenewalDate(
                                            card.account_id,
                                            e.target.value
                                          );
                                        }
                                      }}
                                      disabled={isLoading}
                                      style={{
                                        padding: '4px 8px',
                                        fontSize: '12px',
                                        borderRadius: '4px',
                                        border: '1px solid rgba(255, 255, 255, 0.3)',
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        color: 'white',
                                        cursor: 'pointer',
                                        width: '100%',
                                      }}
                                    />
                                    <small
                                      style={{
                                        fontSize: '10px',
                                        opacity: 0.6,
                                        display: 'block',
                                        marginTop: '2px',
                                      }}
                                    >
                                      Travel credit resets on this date
                                    </small>
                                  </div>
                                )}
                                <button
                                  onClick={() =>
                                    navigate(`/transactions/${card.account_id}`)
                                  }
                                  style={{
                                    marginTop: '8px',
                                    padding: '6px 12px',
                                    fontSize: '12px',
                                    borderRadius: '4px',
                                    border: 'none',
                                    backgroundColor: '#61dafb',
                                    color: '#282c34',
                                    cursor: 'pointer',
                                    width: '100%',
                                  }}
                                >
                                  View Transactions
                                </button>
                                <button
                                  onClick={() => handleResyncAccount(card.account_id)}
                                  disabled={syncingAccountId === card.account_id}
                                  style={{
                                    marginTop: '8px',
                                    padding: '6px 12px',
                                    fontSize: '12px',
                                    borderRadius: '4px',
                                    border: 'none',
                                    backgroundColor:
                                      syncingAccountId === card.account_id
                                        ? '#cccccc'
                                        : '#4caf50',
                                    color: '#282c34',
                                    cursor:
                                      syncingAccountId === card.account_id
                                        ? 'not-allowed'
                                        : 'pointer',
                                    width: '100%',
                                    opacity:
                                      syncingAccountId === card.account_id ? 0.6 : 1,
                                  }}
                                >
                                  {syncingAccountId === card.account_id
                                    ? 'Syncing...'
                                    : 'Resync Transactions'}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <small style={{ opacity: 0.7 }}>
                        Linked: {new Date(account.linked_at).toLocaleDateString()}
                      </small>
                    </div>
                    <button
                      onClick={() =>
                        handleUnlinkAccount(
                          account.item_id,
                          account.institution_name
                        )
                      }
                      disabled={isLoading}
                      style={{
                        padding: '6px 12px',
                        fontSize: '14px',
                        borderRadius: '4px',
                        border: 'none',
                        backgroundColor: '#ff6b6b',
                        color: 'white',
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        opacity: isLoading ? 0.6 : 1,
                      }}
                    >
                      Unlink
                    </button>
                  </li>
                ))}
              </ul>
              )}
            </div>
          )}
          <div style={{ marginTop: '30px', display: 'flex', gap: '15px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              onClick={startPlaidLink}
              disabled={isLoading}
              style={{
                padding: '14px 32px',
                fontSize: '16px',
                fontWeight: '600',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #61dafb 0%, #4facdb 100%)',
                color: '#282c34',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.6 : 1,
                boxShadow: '0 4px 12px rgba(97, 218, 251, 0.3)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(97, 218, 251, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(97, 218, 251, 0.3)';
              }}
            >
              + Link Bank Account
            </button>
            <button
              onClick={logout}
              style={{
                padding: '14px 32px',
                fontSize: '16px',
                fontWeight: '600',
                borderRadius: '12px',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                backgroundColor: 'transparent',
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
              }}
            >
              Logout
            </button>
          </div>
        </header>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1 style={{ fontSize: '48px', marginBottom: '10px', fontWeight: '700' }}>💳 Card Tracker</h1>
        <p style={{ opacity: 0.7, marginBottom: '40px', fontSize: '16px' }}>Track your credit card rewards</p>
        {error && (
          <p style={{
            color: '#ff6b6b',
            marginTop: '15px',
            padding: '12px',
            backgroundColor: 'rgba(255, 107, 107, 0.1)',
            borderRadius: '8px',
            border: '1px solid rgba(255, 107, 107, 0.3)',
          }}>{error}</p>
        )}
        <button
          onClick={login}
          disabled={authLoading}
          style={{
            padding: '16px 40px',
            fontSize: '18px',
            fontWeight: '600',
            borderRadius: '12px',
            border: 'none',
            background: 'white',
            color: '#282c34',
            cursor: authLoading ? 'not-allowed' : 'pointer',
            boxShadow: '0 4px 12px rgba(255, 255, 255, 0.2)',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <img
            src="https://www.google.com/favicon.ico"
            alt="Google"
            style={{ width: '24px', height: '24px' }}
          />
          Sign in with Google
        </button>
      </header>
    </div>
  );
}

export default App;
